// Code-defined navigation registry — the source of truth for routes, icons and
// permissions. The admin org-navigation CONFIG only overrides PRESENTATION
// (zone/priority/hidden/label/order). Permissions are NEVER changed by config.
import {
  Home, FileText, Instagram, Youtube, Megaphone, Library, BarChart3,
  ListTodo, Users, FileBarChart, Settings as SettingsIcon,
  LayoutDashboard, AlertCircle, ListChecks, BadgeCheck, CalendarDays,
  Bookmark, Activity, HeartPulse, Clock, Circle, Lightbulb,
} from "lucide-react";

export const DEFAULT_SIDEBAR_ICON = Circle;

// perm = required permission string (from Roles); null = visible to all authed.
// Owner/Super Admin/Admin bypass all perms.
export const WORKSPACES = [
  { id: "home", label: "Home", icon: Home, route: "/", perm: null, priority: "high", zone: "top" },
  { id: "blogs", label: "Blogs", icon: FileText, route: "/blogs", perm: null, priority: "high", zone: "top" },
  { id: "instagram", label: "Instagram", icon: Instagram, route: "/instagram", perm: null, priority: "high", zone: "top" },
  { id: "youtube", label: "YouTube", icon: Youtube, route: "/youtube", perm: null, priority: "high", zone: "top" },
  { id: "campaigns", label: "Campaigns", icon: Megaphone, route: "/campaigns", perm: null, priority: "medium", zone: "top" },
  { id: "knowledge", label: "Knowledge", icon: Library, route: "/knowledge", perm: null, priority: "medium", zone: "top" },
  { id: "performance", label: "Performance", icon: BarChart3, route: "/performance", perm: null, priority: "medium", zone: "top" },
  { id: "topics", label: "Topics & Ideas", icon: Lightbulb, route: "/topics", perm: null, priority: "high", zone: "top" },
  { id: "tasks", label: "My Tasks", icon: ListTodo, route: "/tasks", perm: null, priority: "high", zone: "top" },
  { id: "team", label: "Team", icon: Users, route: "/team/members", perm: null, priority: "medium", zone: "top" },
  { id: "reports", label: "Reports", icon: FileBarChart, route: "/reports", perm: null, priority: "low", zone: "more" },
  { id: "settings", label: "Settings", icon: SettingsIcon, route: "/settings", perm: "Manage Settings", priority: "low", zone: "more" },
];

const R = (id, label, route, ready = false, perm = null) => ({ id, label, route, ready, perm });

// Level-2 contextual sidebars keyed by workspace id (matched by route prefix).
export const SIDEBARS = {
  blogs: [
    R("blogs-overview", "Overview", "/blogs"), R("blogs-ideas", "Ideas", "/blogs/ideas"),
    R("blogs-topics", "Topics", "/blogs/topics"), R("blogs-progress", "In Progress", "/blogs/in-progress"),
    R("blogs-reviews", "Reviews", "/blogs/reviews"), R("blogs-ready", "Ready to Publish", "/blogs/ready"),
    R("blogs-published", "Published", "/blogs/published"), R("blogs-perf", "Performance", "/blogs/performance"),
    R("blogs-links", "Internal Links", "/blogs/links"), R("blogs-videos", "Videos", "/blogs/videos"),
    R("blogs-schedule", "Review Schedule", "/blogs/schedule"),
  ],
  instagram: [
    R("ig-overview", "Overview", "/instagram"), R("ig-ideas", "Ideas", "/instagram/ideas"),
    R("ig-reels", "Reels", "/instagram/reels"), R("ig-carousels", "Carousels", "/instagram/carousels"),
    R("ig-production", "Production", "/instagram/production"), R("ig-reviews", "Reviews", "/instagram/reviews"),
    R("ig-scheduled", "Scheduled", "/instagram/scheduled"), R("ig-published", "Published", "/instagram/published"),
    R("ig-perf", "Performance", "/instagram/performance"), R("ig-hooks", "Hooks", "/instagram/hooks"),
    R("ig-questions", "Audience Questions", "/instagram/questions"),
  ],
  youtube: [
    R("yt-overview", "Overview", "/youtube"), R("yt-ideas", "Ideas", "/youtube/ideas"),
    R("yt-long", "Long-form", "/youtube/long-form"), R("yt-shorts", "Shorts", "/youtube/shorts"),
    R("yt-scripts", "Scripts", "/youtube/scripts"), R("yt-production", "Production", "/youtube/production"),
    R("yt-editing", "Editing", "/youtube/editing"), R("yt-thumbs", "Thumbnails", "/youtube/thumbnails"),
    R("yt-reviews", "Reviews", "/youtube/reviews"), R("yt-ready", "Ready to Publish", "/youtube/ready"),
    R("yt-published", "Published", "/youtube/published"), R("yt-perf", "Performance", "/youtube/performance"),
  ],
  campaigns: [
    R("cm-overview", "Overview", "/campaigns"), R("cm-active", "Active", "/campaigns/active"),
    R("cm-upcoming", "Upcoming", "/campaigns/upcoming"), R("cm-calendar", "Calendar", "/campaigns/calendar"),
    R("cm-completed", "Completed", "/campaigns/completed"),
  ],
  knowledge: [
    R("kn-overview", "Overview", "/knowledge"), R("kn-sources", "Source Library", "/knowledge/sources"),
    R("kn-materials", "Vijayalakshmi Materials", "/knowledge/materials"), R("kn-transcripts", "Transcripts", "/knowledge/transcripts"),
    R("kn-questions", "Audience Questions", "/knowledge/questions"), R("kn-media", "Media Library", "/knowledge/media"),
    R("kn-decisions", "Decisions & Guidelines", "/knowledge/decisions"),
  ],
  performance: [
    R("pf-overview", "Overview", "/performance"), R("pf-blogs", "Blogs", "/performance/blogs"),
    R("pf-ig", "Instagram", "/performance/instagram"), R("pf-yt", "YouTube", "/performance/youtube"),
    R("pf-topics", "Topics", "/performance/topics"), R("pf-hooks", "Hooks", "/performance/hooks"),
    R("pf-insights", "Content Insights", "/performance/insights"), R("pf-reviews", "Performance Reviews", "/performance/reviews"),
  ],
  tasks: [
    R("tk-inbox", "Inbox", "/tasks/inbox", true), R("tk-today", "Today", "/tasks", true),
    R("tk-assigned", "Assigned to Me", "/tasks/assigned", true), R("tk-personal", "Personal", "/tasks/personal", true),
    R("tk-reminders", "Reminders", "/tasks/reminders", true), R("tk-waiting", "Waiting", "/tasks/waiting", true),
    R("tk-completed", "Completed", "/tasks/completed", true),
    R("tk-boards", "Boards", "/tasks/boards", true),
  ],
  team: [
    R("tm-members", "Members", "/team/members", true), R("tm-roles", "Roles", "/settings/roles", true, "Manage Roles"),
    R("tm-assignments", "Assignments", "/team/assignments"), R("tm-activity", "Activity", "/team/activity", true, "View Activity History"),
    R("tm-reports", "Daily Reports", "/team/reports"), R("tm-workload", "Workload", "/team/workload"),
  ],
  reports: [
    R("rp-overview", "Overview", "/reports"), R("rp-daily", "Daily Reports", "/reports/daily"),
    R("rp-weekly", "Weekly Reports", "/reports/weekly"), R("rp-content", "Content Reports", "/reports/content"),
    R("rp-performance", "Performance Reports", "/reports/performance"),
  ],
  settings: [
    R("st-org", "Organisation", "/settings/organisation", true, "Manage Settings"),
    R("st-appearance", "Appearance", "/settings/appearance", true, "Manage Settings"),
    R("st-nav", "Navigation", "/settings/navigation", true, "Manage Settings"),
    R("st-language", "Language & Labels", "/settings/language", true, "Manage Settings"),
    R("st-roles", "Roles & Permissions", "/settings/roles", true, "Manage Roles"),
    R("st-checklists", "Checklists", "/settings/checklists", true, "view_checklist_templates"),
    R("st-recipients", "Report Recipients", "/settings/recipients", true, "Manage Settings"),
    R("st-integrations", "Integrations", "/settings/integrations", true, "Manage Integrations"),
    R("st-notifications", "Notifications", "/settings/notifications", false, "Manage Settings"),
    R("st-health", "System Health", "/settings/health", false, "Manage Settings"),
  ],
};

// Level-3 reusable page-tab sets (for individual content records — future).
export const PAGE_TABS = {
  blog: ["Overview", "Brief", "Draft", "Sources", "SEO", "Reviews", "Links & Videos", "History", "Performance"],
  reel: ["Overview", "Script", "Sources", "Production", "Review", "Publishing", "Performance", "History"],
  youtube: ["Overview", "Research", "Outline", "Script", "Sources", "Production", "Editing", "Publishing", "Performance", "History"],
};

export const QUICK_CREATE = [
  { id: "idea", label: "Idea", perm: null }, { id: "blog", label: "Blog", perm: null },
  { id: "reel", label: "Reel", perm: null }, { id: "carousel", label: "Carousel", perm: null },
  { id: "youtube", label: "YouTube Video", perm: null }, { id: "campaign", label: "Campaign", perm: null },
  { id: "task", label: "Task", perm: null }, { id: "reminder", label: "Reminder", perm: null },
  { id: "question", label: "Audience Question", perm: null },
];

const ADMIN_ROLES = ["Owner", "Super Admin", "Admin"];

// ---------- Home / Dashboard contextual sidebar ----------
// icon-bearing items so the compact rail can render properly. Routes without a
// built module resolve to the friendly "Not available yet" page (no fake data).
const H = (id, label, route, icon, opts = {}) =>
  ({ id, label, route, icon, ready: !!opts.ready, perm: opts.perm || null, adminOnly: !!opts.adminOnly });

export const HOME_SIDEBAR = [
  H("home-overview", "Overview", "/", LayoutDashboard, { ready: true }),
  H("home-needs", "Needs Attention", "/home/needs-attention", AlertCircle),
  H("home-mywork", "My Work", "/home/my-work", ListChecks),
  H("home-approvals", "Approvals", "/home/approvals", BadgeCheck, { ready: true }),
  H("home-calendar", "Calendar", "/home/calendar", CalendarDays),
  H("home-views", "Saved Views", "/home/saved-views", Bookmark),
  H("home-activity", "Recent Activity", "/team/activity", Activity, { ready: true, perm: "View Activity History" }),
  // Owner / Admin foundation items
  H("home-team", "Team Overview", "/team/members", Users, { ready: true, adminOnly: true }),
  H("home-reports", "Reports", "/reports", FileBarChart, { adminOnly: true }),
  H("home-health", "System Health", "/settings/health", HeartPulse, { adminOnly: true, perm: "Manage Settings" }),
  // Foundation-only items referenced by role defaults (workflows arrive in later releases)
  H("home-waiting", "Waiting on Team", "/home/waiting", Clock, { adminOnly: true }),
  H("home-performance", "Performance", "/performance", BarChart3, { adminOnly: true }),
  H("home-assigned", "Assigned Topics", "/home/assigned", ListChecks),
  H("home-drafts", "Drafts", "/home/drafts", FileText),
  H("home-returned", "Returned for Changes", "/home/returned", AlertCircle),
  H("home-deadlines", "Upcoming Deadlines", "/home/deadlines", CalendarDays),
  H("home-reviews-waiting", "Reviews Waiting", "/home/reviews-waiting", BadgeCheck),
  H("home-source-questions", "Source Questions", "/home/source-questions", Library),
  H("home-changed", "Changed After Approval", "/home/changed", AlertCircle),
  H("home-upcoming-reviews", "Upcoming Reviews", "/home/upcoming-reviews", CalendarDays),
  H("home-verified", "Recently Verified", "/home/verified", BadgeCheck),
];

// Role Starter foundation: per-role default Home sidebar ORDER (ids). The resolver
// silently drops ids whose module/permission is not available for the user, so
// future workflow items can be listed here safely today.
export const HOME_SIDEBAR_ROLE_DEFAULTS = {
  "Owner": ["home-overview", "home-needs", "home-approvals", "home-waiting", "home-calendar", "home-team", "home-performance", "home-health"],
  "Super Admin": ["home-overview", "home-needs", "home-approvals", "home-waiting", "home-calendar", "home-team", "home-performance", "home-health"],
  "Content Writer": ["home-overview", "home-mywork", "home-assigned", "home-drafts", "home-returned", "home-deadlines"],
  "Authenticity Reviewer": ["home-overview", "home-reviews-waiting", "home-source-questions", "home-changed", "home-upcoming-reviews", "home-verified"],
};

export function canAccess(item, ctx) {
  if (item.adminOnly && !ADMIN_ROLES.includes(ctx.role)) return false;
  if (!item.perm) return true;
  if (ADMIN_ROLES.includes(ctx.role)) return true;
  return (ctx.permissions || []).includes(item.perm);
}

// Resolve the Home sidebar: admin org override → role default → base order,
// then apply admin label/visibility overrides and permission filtering.
export function resolveHomeSidebar(config, ctx) {
  const cfg = config?.home || {};
  const byId = Object.fromEntries(HOME_SIDEBAR.map((i) => [i.id, i]));
  let order = cfg.order && cfg.order.length ? cfg.order
    : (HOME_SIDEBAR_ROLE_DEFAULTS[ctx.role] || HOME_SIDEBAR.slice(0, 7).map((i) => i.id));
  return order
    .map((id) => byId[id])
    .filter(Boolean)
    .map((it) => {
      const ov = cfg.items?.[it.id] || {};
      return { ...it, ...ov, label: ov.label || it.label };
    })
    .filter((it) => !it.hidden && canAccess(it, ctx));
}

// Merge admin config overrides onto workspace registry + permission filter.
export function resolveWorkspaces(config, ctx) {
  const ov = config?.workspaces || {};
  return WORKSPACES
    .map((w) => ({ ...w, ...(ov[w.id] || {}), label: (ov[w.id]?.label) || w.label }))
    .filter((w) => !w.hidden && canAccess(w, ctx));
}

export function resolveSidebar(wsId, config, ctx) {
  const items = SIDEBARS[wsId] || [];
  const ov = config?.sidebars?.[wsId] || {};
  return items
    .map((it) => ({ ...it, ...(ov[it.id] || {}), label: (ov[it.id]?.label) || it.label }))
    .filter((it) => !it.hidden && canAccess(it, ctx));
}

// Determine workspace id from a pathname.
export function workspaceFromPath(pathname) {
  const seg = pathname.split("/").filter(Boolean)[0] || "home";
  const map = { "": "home", home: "home", blogs: "blogs", instagram: "instagram", youtube: "youtube",
    campaigns: "campaigns", knowledge: "knowledge", performance: "performance",
    tasks: "tasks", team: "team", reports: "reports", settings: "settings" };
  return map[seg] || "home";
}
